import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

// project imports
import useAuth from 'hooks/useAuth';
import { useWorkspace } from 'contexts/WorkspaceContext';
import { usePermissions, MANAGEMENT_ROLES } from 'contexts/PermissionsContext';
import Loader from 'components/Loader';
import MainCard from 'components/MainCard';
import Logo from 'components/logo';

// types
import { GuardProps } from 'types/auth';

// ==============================|| SETUP GUARD ||============================== //

/**
 * SetupGuard redirects HOA users who haven't completed setup to /setup
 * Non-admin users without organization see a message to contact their admin
 */
export default function SetupGuard({ children }: GuardProps) {
  const { isLoggedIn, isInitialized, user, onboardingComplete } = useAuth() as any;
  const { hasManagementRole, loading: workspaceLoading, activeCommunityId } = useWorkspace();
  const { roleNames, isLoading: permissionsLoading, permissionsEnabled } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  // Ready to decide once workspace is loaded AND permissions have resolved (if they were enabled)
  const readyToDecide = !workspaceLoading && (!permissionsEnabled || !permissionsLoading);

  // ── Derived values (computed synchronously — no useState/useEffect gap) ──
  let isOrgAdmin = false;
  let isPrivilegedMember = false;
  let setupComplete = false;

  if (isInitialized && isLoggedIn && user && readyToDecide) {
    const userId = (user as any).documentId || String((user as any).id);
    isOrgAdmin = user.organization
      ? Array.isArray(user.organization?.admins) &&
        user.organization!.admins!.some(
          (a: any) => a.documentId === userId || String((a as any).id) === userId
        )
      : (typeof user.role === 'string' ? user.role : (user.role as any)?.name) === 'HOA';

    isPrivilegedMember =
      hasManagementRole ||
      roleNames.some((n) => MANAGEMENT_ROLES.has(n));

    const hasOrganization = user?.organization !== null && user?.organization !== undefined;
    const hasCommunity =
      hasOrganization &&
      Array.isArray(user?.organization?.communities) &&
      user.organization!.communities!.length > 0;
    setupComplete = hasOrganization && hasCommunity;
  }

  const isOnSetupPage = location.pathname.startsWith('/setup');

  // Navigation side-effects only — UI state is derived inline above.
  useEffect(() => {
    if (!isInitialized || !isLoggedIn || !user || !readyToDecide) return;

    // Org admin without completed setup should go to /setup.
    // Guard with !permissionsEnabled: a community role member may also have
    // Strapi role 'HOA' but is NOT a new HOA admin needing onboarding.
    // A genuine new HOA user has no active community yet, so permissionsEnabled = false.
    if (isOrgAdmin && !setupComplete && !isOnSetupPage && !permissionsEnabled) {
      navigate('/setup', { replace: true });
    }

    // If setup is complete and on setup page, redirect to dashboard
    if ((isOrgAdmin && setupComplete && isOnSetupPage) || (isPrivilegedMember && isOnSetupPage)) {
      navigate('/dashboard', { replace: true });
    }

    // Non-admin users on setup page should be redirected to dashboard
    if (!isOrgAdmin && !isPrivilegedMember && isOnSetupPage) {
      navigate('/dashboard', { replace: true });
    }
  }, [isInitialized, isLoggedIn, user, navigate, isOnSetupPage, isOrgAdmin, isPrivilegedMember, setupComplete, permissionsEnabled, readyToDecide]);

  if (!isInitialized || !readyToDecide) {
    return <Loader />;
  }

  // Non-admin users without a configured org should see contact admin message.
  const showContactAdmin = !isOrgAdmin && !isPrivilegedMember && !setupComplete;

  // Org + community exists but wizard not done — block non-management members.
  const showCommunityNotReady =
    !isOrgAdmin && !isPrivilegedMember && setupComplete && !onboardingComplete;

  if (showContactAdmin) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Logo to="/" />
          </Box>
          <MainCard>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h4" gutterBottom>
                Account Configuration Required
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your account has not yet been configured.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please contact your HOA administrator to complete the setup process.
              </Typography>
            </Box>
          </MainCard>
        </Container>
      </Box>
    );
  }

  if (showCommunityNotReady) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'background.default',
          zIndex: (theme) => theme.zIndex.modal + 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Logo to="/" />
          </Box>
          <MainCard>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h4" gutterBottom>
                Almost There!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your community is currently being set up by your administrator.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                You'll have full access once the setup is complete.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Check back soon or reach out to your HOA admin for an update.
              </Typography>
            </Box>
          </MainCard>
        </Container>
      </Box>
    );
  }

  return children;
}
